import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Box, BoxProps } from '@chakra-ui/react';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Placeholder } from '@tiptap/extensions/placeholder';
import { NodeSelection } from '@tiptap/pm/state';
import { Tiptap, useEditor } from '@tiptap/react';

import styles from './editor.module.scss';
import {
  ComposedMessage,
  composeMessage,
  insertPromptChip,
  PromptChipInput,
  PromptChipPopover,
  PromptUse,
  SlashExtension,
  slashExtensionNodes,
  SuggestionItemGroup,
  useSuggestions,
} from './extensions/SlashExtension';

export type EditorHandle = {
  /** Inserts a chip at the caret: a template, or today's string (title = body). */
  insertSlashCommand: (item: PromptChipInput) => boolean;
  /** The composed text and the `prompts` record, as `onChange` last sent them. */
  getComposedMessage: () => ComposedMessage;
  /** How many chips of this Prompt Template the composer holds. */
  countPromptChips: (templateId: number) => number;
  /**
   * Opens the fill step of the last chip of this Prompt Template, as a click
   * on it would (Use prompt landing with Context Variables, #632).
   * `provisional`: the chip still has an empty field, so closing the step
   * without saving removes the chip, as if it never landed.
   */
  openPromptChip: (
    templateId: number,
    options?: { provisional?: boolean }
  ) => boolean;
  focus: () => void;
};

export type EditorProps = {
  /**
   * The composed message: chip bodies and free text in document order,
   * joined with `\n` (equal to the plain text when there are no chips), and
   * the `prompts` record for chips with a template.
   */
  onChange?: (content: string, prompts: PromptUse[]) => void;
  disabled?: boolean;
  value?: string;
  placeholder?: string;
  children?: React.ReactNode;
  suggestions: SuggestionItemGroup[];
  /** The agent whose prompts the `/` menu shows; its Recent row needs it. */
  agentId?: number;
  onSubmit?: () => void;
  /** Extra room for the command picker on the left (default 12px). */
  contentPaddingLeft?: number | string;
  /** Extra room for action buttons on the right (default 48px). */
  contentPaddingRight?: number | string;
} & Omit<BoxProps, 'onChange'>;

/** A chip from a Prompt Template (switch-off strings stay as they are). */
const isReopenableChip = (node: { type: { name: string }; attrs: any }) =>
  node.type.name === 'promptChip' && node.attrs.templateId != null;

const toPadding = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

export const Editor = forwardRef<EditorHandle, EditorProps>(function Editor(
  {
    onChange,
    disabled,
    value,
    placeholder,
    children,
    suggestions: baseSuggestions,
    agentId,
    onSubmit,
    contentPaddingLeft = 12,
    contentPaddingRight = 48,
    ...props
  },
  ref
) {
  // Stable options that read the latest groups: the editor is built once, so
  // new groups (a template saved, the commands refetched) keep its chips.
  const suggestion = useSuggestions(baseSuggestions, agentId);

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  // A Prompt Template chip reopened for this message (VH3_CONNECT #592).
  const [openChipPos, setOpenChipPos] = useState<number | null>(null);
  const openChipRef = useRef(setOpenChipPos);
  // The chip a Use prompt landing opened with a field still empty (#632).
  const [provisionalChipPos, setProvisionalChipPos] = useState<number | null>(
    null
  );

  const editor = useEditor(
    {
      extensions: [
        Document,
        Paragraph,
        Text,
        HardBreak.extend({
          addKeyboardShortcuts() {
            return {
              'Shift-Enter': () => this.editor.commands.setHardBreak(),
            };
          },
        }),

        ...slashExtensionNodes,

        Placeholder.configure({
          placeholder: placeholder ?? '',
        }),
        SlashExtension.configure({ suggestion }),
      ],

      content: value || '',

      editorProps: {
        handleKeyDown(view, event) {
          const isEnter = event.key === 'Enter';
          const isPlainEnter = isEnter && !event.shiftKey;
          const isSuggestionActive = (view.state as any).suggestion$?.active;

          if (isPlainEnter && !isSuggestionActive) {
            // Enter on a selected template chip reopens it instead of sending.
            const { selection } = view.state;
            if (
              selection instanceof NodeSelection &&
              isReopenableChip(selection.node)
            ) {
              openChipRef.current(selection.from);
              return true;
            }
            onSubmitRef.current?.();
            return true;
          }
        },
        handleDOMEvents: {
          // A click on a template chip reopens it, selected or not (ProseMirror
          // treats a click on the selected node as a drag, so no handleClickOn).
          click(view, event) {
            const chip = (event.target as HTMLElement | null)?.closest?.(
              'span[data-type="prompt-chip"]'
            );
            if (!chip || !view.editable || !view.dom.contains(chip)) {
              return false;
            }
            let chipPos: number | null = null;
            view.state.doc.descendants((node, pos) => {
              if (chipPos !== null) return false;
              if (isReopenableChip(node) && view.nodeDOM(pos) === chip) {
                chipPos = pos;
              }
            });
            if (chipPos === null) return false;
            event.preventDefault();
            openChipRef.current(chipPos);
            return true;
          },
        },
      },
    },
    []
  );

  // Texts sent through `onChange` that `value` has not caught up with yet, oldest
  // first. The parent's render for an older text can land after the next
  // keystroke; resetting the document to it would drop that key.
  const pendingEchoesRef = useRef<string[]>([]);

  useEffect(() => {
    if (!editor) return;

    const handler = () => {
      const { text, prompts } = composeMessage(editor);
      pendingEchoesRef.current.push(text);
      onChange?.(text, prompts);
    };

    editor.on('update', handler);

    return () => {
      editor.off('update', handler);
    };
  }, [editor, onChange]);

  useEffect(() => {
    if (!editor) return;
    const pending = pendingEchoesRef.current;

    if (composeMessage(editor).text === value) {
      pending.length = 0;
      return;
    }

    // An older echo of our own text: skip it and anything sent before it.
    const echoIndex = pending.indexOf(value ?? '');
    if (echoIndex !== -1) {
      pending.splice(0, echoIndex + 1);
      return;
    }

    // A change from outside, such as clearing after send.
    editor.commands.setContent(value || '', { emitUpdate: false });
    pending.length = 0;
  }, [editor, value]);

  // A new editor has new positions.
  useEffect(() => setOpenChipPos(null), [editor]);

  const closeChip = useCallback(
    (refocus: boolean, saved = false) => {
      const closing = openChipPos;
      setOpenChipPos(null);
      setProvisionalChipPos(null);
      // A landed chip that was never filled in goes, so nothing is sent with
      // a `{name}` left in; the user can pick the prompt again.
      if (!saved && closing !== null && closing === provisionalChipPos) {
        const node = editor?.state.doc.nodeAt(closing);
        if (node?.type.name === 'promptChip') {
          editor
            ?.chain()
            .focus()
            .deleteRange({ from: closing, to: closing + node.nodeSize })
            .run();
          return;
        }
      }
      if (refocus) editor?.commands.focus();
    },
    [editor, openChipPos, provisionalChipPos]
  );

  useImperativeHandle(
    ref,
    () => ({
      insertSlashCommand: item => {
        if (!editor) return false;
        insertPromptChip({ editor, chip: item });
        return true;
      },
      getComposedMessage: () =>
        editor ? composeMessage(editor) : { text: '', prompts: [] },
      countPromptChips: templateId => {
        let count = 0;
        editor?.state.doc.descendants(node => {
          if (
            node.type.name === 'promptChip' &&
            node.attrs.templateId === templateId
          ) {
            count += 1;
          }
        });
        return count;
      },
      openPromptChip: (templateId, options) => {
        let pos: number | null = null;
        editor?.state.doc.descendants((node, nodePos) => {
          if (
            node.type.name === 'promptChip' &&
            node.attrs.templateId === templateId
          ) {
            pos = nodePos;
          }
        });
        if (pos === null) return false;
        setProvisionalChipPos(options?.provisional ? pos : null);
        setOpenChipPos(pos);
        return true;
      },
      focus: () => {
        editor?.commands.focus();
      },
    }),
    [editor]
  );

  return (
    <Box
      minH='48px'
      maxH='160px'
      borderRadius={12}
      fontSize={16}
      lineHeight={6}
      bg='search-input.bg'
      color='white'
      border='1px solid'
      borderColor='search-input.border!'
      _hover={{
        bg: 'search-input.bg-hover',
        borderColor: 'search-input.border-hover!',
      }}
      _focusWithin={{
        bg: 'search-input.bg-active!',
        borderColor: 'search-input.border-hover!',
      }}
      _disabled={{
        bg: 'search-input.bg-disabled!',
        color: 'search-input.text-disabled!',
        borderColor: 'search-input.border-disabled!',
      }}
      boxShadow='none!'
      position='relative'
      className={styles.editor}
      data-prompt-composer=''
      {...props}
    >
      <Tiptap instance={editor}>
        <Tiptap.Content
          style={{
            maxHeight: '100%',
            overflowY: 'auto',
            padding: `12px ${toPadding(contentPaddingRight)} 12px ${toPadding(
              contentPaddingLeft
            )}`,
          }}
          disabled={disabled || !editor.isInitialized}
        />
      </Tiptap>
      {editor && openChipPos !== null && (
        <PromptChipPopover
          editor={editor}
          pos={openChipPos}
          onClose={closeChip}
        />
      )}
      {children}
    </Box>
  );
});
